<template>
	<Pupup
		v-if="popup.show"
		:showClose="true"
		title=""
		:width="1360"
		:full="true"
		:clickOverlayToClose="false"
		:shuffleUp="true"
		@close="handleClose">
		<template #content>
			<div class="collab-guide">
				<h2 class="underline main-title"><span>Let's Get You Ready for Your First Collab!</span></h2>
				<section class="guide-hero">
					<aside class="hero-tip tip">
						<p class="tip-label">💡 Top Tip</p>
						<div class="tip-panel">
							<p>
								A great place to start is our <span class="link" @click="handleGoTo('/opportunities')">Opportunities Area</span>,
								where you can gather ideas and join collaborations posted by other brands who can help guide you through your first
								collaboration.
							</p>
						</div>
					</aside>
				</section>

				<div class="guide-layout">
					<div class="guide-column guide-column--main">
						<section
							v-for="(section, index) in guideSections"
							:key="section.id"
							:class="['guide-section', { 'guide-section--reverse': index % 2 === 1 }]">
							<div class="section-heading">
								<div class="section-visual" aria-hidden="true">
									<div class="visual-card">
										<img :src="section.image" alt="" loading="lazy" />
									</div>
								</div>

								<div class="section-copy">
									<h3 class="underline small">
										<span>{{ section.title }}</span>
									</h3>
									<p v-for="paragraph in section.copy" :key="paragraph" v-html="paragraph" />

									<button
										v-if="section.example || section.tip || section.placeholder"
										type="button"
										class="guide-toggle"
										:aria-expanded="isGuideSectionOpen(section.id)"
										@click="toggleGuideSection(section.id)">
										{{ isGuideSectionOpen(section.id) ? "Show less" : getGuideSectionToggleLabel(section) }}
									</button>
								</div>
							</div>

							<div v-if="isGuideSectionOpen(section.id) && section.example" class="callout-box">
								<h3 class="callout-title">{{ section.exampleLabel || "Examples" }}</h3>
								<ul class="callout-list">
									<li v-for="item in section.example" :key="item">{{ item }}</li>
								</ul>
							</div>

							<div v-if="isGuideSectionOpen(section.id) && section.tip" class="callout-box callout-box--tip tip">
								<p class="tip-label">💡 Top Tip</p>
								<div class="tip-panel">
									<p v-html="section.tip" />
								</div>
							</div>

							<div v-if="isGuideSectionOpen(section.id) && section.placeholder" class="callout-box">
								<h3 class="callout-title">Example</h3>
								<p class="example-text" v-html="section.placeholder" />
							</div>
						</section>
					</div>

					<div class="guide-column guide-column--side">
						<section class="side-panel">
							<div class="panel-header">
								<h3 class="underline small"><span>Collabs recommended for beginners</span></h3>
							</div>

							<CollabTypes class="starter-types" :showTCs="false" :filterByTitles="starterCollabs" maxPerRow="2" mini />
						</section>

						<section class="side-panel side-panel--faq">
							<div class="panel-header">
								<h3 class="underline small"><span>Need more info? We've got it!</span></h3>
							</div>

							<div class="faq-list">
								<div v-for="item in faqItems" :key="item.id" class="faq-item">
									<button type="button" class="faq-trigger" :aria-expanded="isFaqOpen(item.id)" @click="toggleFaq(item.id)">
										<span v-html="item.question" />
										<span class="faq-icon" aria-hidden="true">{{ isFaqOpen(item.id) ? "−" : "+" }}</span>
									</button>

									<div v-if="isFaqOpen(item.id)" class="faq-body">
										<p v-for="paragraph in item.answer" :key="paragraph" v-html="paragraph" />

										<ul v-if="item.list && item.list.length" class="faq-bullets">
											<li v-for="entry in item.list" :key="entry" v-html="entry" />
										</ul>
									</div>
								</div>
							</div>
						</section>
					</div>
				</div>
			</div>
		</template>
	</Pupup>
</template>

<script>
import { computed, ref } from "vue";
import { useVueInstance } from "../../composables/useVueInstance";
import CollabTypes from "../Display/CollabTypes.vue";
import Pupup from "../Display/Pupup.vue";

export default {
	name: "CollabHelpNeverDone",
	components: {
		CollabTypes,
		Pupup,
	},
	setup() {
		const { store, router } = useVueInstance();
		const popup = computed(() => store.state.general.collabHelpNeverDonePopup || {});
		const openFaqIds = ref([]);
		const openGuideSectionIds = ref([]);

		const guideSections = [
			{
				id: "planning",
				title: "Planning your collaborations",
				image: require("../../assets/img/doi-medium.jpg"),
				copy: [
					"Your collaborations should support your business goals. <br>Identify what you need to achieve and plan partnerships that feed into those goals.",
					"For example, if your goal is to grow newsletter subscribers, plan a Newsletter Giveaway. To take this one step further, plan the Giveaway a few months before a key trading moment, so the new subscribers have time to learn about your brand and purchase when the moment is right.",
					"Plan your collaborations alongside your wider marketing activities, or as early as possible, you avoid last minute stress, budget issues or stock shortages.",
				],
				example: [
					"Grow social following -> Multi-brand giveaways, Instagram Lives, co-created content",
					"Grow newsletter -> Multi-brand giveaways",
					"Drive revenue -> Discount swaps, product samples, customer rewards",
					"Boost SEO -> Blog swaps with backlinks",
				],
			},
			{
				id: "brand-fit",
				title: "Finding the right brand to partner with",
				image: require("../../assets/img/pooch-mutt2.jpg"),
				copy: [
					"Your PartnerUp Matches are based on the information you provided in your profile set up. We align you based on customers and values.",
					"Check their website, social channels, tone of voice and reviews to ensure they are a good fit.",
					"The collaboration should provide value to both brands and audiences.",
				],
				tip: "Size is not everything when choosing brand partners. Smaller brands often have a more engaged and loyal following, which can make the collaboration stronger.",
			},
			{
				id: "outreach",
				title: "How to write your outreach message",
				image: require("../../assets/img/brand-connect.jpg"),
				copy: [
					"Messaging brands should be like a sales pitch. Everyone is busy and working to their own plans, so you want to make it as easy as possible for them to say yes.",
					"Come prepared with a clear concept, explain why your brands are a good fit, and what will be the benefit to them.",
					"Even with a smaller following, you still have value whether through your audience, product, expertise or engagement.",
				],
				placeholder:[
					"Hi Dusk team, <br><br>I'm Jess, co-founder of Fern & Folk. We make small-batch natural candles and home fragrance for people who are intentional about what they bring into their space. We have around 18k newsletter subscribers, mostly women 28-42 who are deeply into slow living and conscious consumption. <br><br>I've been following Dusk Wellness for a while and your audience feels like a mirror of ours, same values, same lifestyle, just a different corner of it. <br><br>I'd love to run a joint newsletter giveaway together. Both brands contribute a small bundle, both newsletters promote it over the same two-week window, and both audiences get introduced to a brand they'll genuinely love. Low effort, real reach. <br><br>Let me know what you think! <br><br>Thanks, <br>Jess"
				],
				tip: "Our AI writing tool can help you write the perfect outreach and give you a strong first draft to edit.",
			},
		];

		const starterCollabs = [
			"Instagram Giveaway",
			"Newsletter giveaway",
			"Discount swap",
			"Instagram Live",
			"Sample inserts",
			"Guest blog post",
			"Content Creation",
		];

		const faqItems = [
			{
				id: "frequency",
				question: "How many collaborations should I do each year?",
				answer: [
					"Collaborations have a lot of benefits, but you still want to be strategic so you do not overwhelm your audience or your team.",
					"Use the guidance below as a starting point rather than a hard rule. Some formats will suit your brand and capacity better than others.",
				],
				list: [
					"<strong>Social media giveaways</strong> -> No more than once per month",
					"<strong>Newsletter giveaways</strong> -> No more than once every 2 months",
					"<strong>Discount swaps</strong> -> No more than once every 2 months",
					"<strong>Blog post swaps</strong> -> No more than once every 2 months",
					"<strong>Adding other brands' samples to your orders</strong> -> Once per quarter",
					"<strong>Your samples in other brands' orders</strong> -> Depends on your budgets",
					"<strong>Gift bundles</strong> -> 1 to 2 times per year",
					"<strong>Co-branded product</strong> -> 1 to 2 times per year",
					"<strong>Events</strong> -> 1 to 2 times per year",
				],
			},
			{
				id: "timing",
				question: "How long do collaborations take to organise and implement?",
				answer: [
					"This depends on the type of collaboration and how your business operates.",
					"These timings are rough guidelines and do not include the outreach and partner-securing stage, which takes additional time.",
				],
				list: [
					"<strong>Discount swap</strong> -> 2 to 4 weeks",
					"<strong>Content creation</strong> -> 2 to 4 weeks",
					"<strong>Guest blog post</strong> -> 2 to 4 weeks",
					"<strong>'Friends of' mention</strong> -> 2 to 4 weeks",
					"<strong>Instagram Live</strong> -> 1 to 2 months",
					"<strong>Instagram giveaway</strong> -> 1 to 2 months",
					"<strong>Subscription milestone</strong> -> 1 to 2 months",
					"<strong>Downloadable guide or gift guide</strong> -> 1 to 2 months",
					"<strong>Newsletter giveaway</strong> -> 2 to 3 months",
					"<strong>Sampling</strong> -> 2 to 3 months",
					"<strong>Gift bundle</strong> -> 3 to 5 months",
					"<strong>Co-branded product</strong> -> 4 to 6 months",
				],
			},
			{
				id: "roi",
				question: "How can I track ROI on my brand collaborations?",
				answer: [
					"The right metric depends on the format, but the principle is the same: define the outcome you care about before the collab starts and measure that outcome consistently.",
				],
				list: [
					"<strong>Discount swap</strong> -> Discount code usage, open rate, click-through rate",
					"<strong>Content creation</strong> -> Follower increase, engagement rate",
					"<strong>Guest blog post</strong> -> Backlinks, Google rankings, analytics traffic",
					"<strong>'Friends of' mention</strong> -> Follower increase, engagement rate",
					"<strong>Instagram Live</strong> -> Website visits, follower growth, live engagement",
					"<strong>Instagram giveaway</strong> -> Follower increase, engagement during and after the giveaway",
					"<strong>Subscription milestone</strong> -> New customers, new reviews",
					"<strong>Downloadable guide or gift guide</strong> -> Downloads",
					"<strong>Newsletter giveaway</strong> -> Subscriber growth, open rate after the giveaway",
					"<strong>Sampling</strong> -> New customers, new reviews, increase in search results",
					"<strong>Gift bundle</strong> -> New customers, extra revenue, website traffic",
					"<strong>Co-branded product</strong> -> New customers, extra revenue, website traffic",
				],
			},
			{
				id: "repeat",
				question: "Is it okay to collaborate with the same brand more than once?",
				answer: [
					"Yes. If your first collaboration was successful, continuing to work together is usually a strong move.",
					"Repeat partnerships help build trust, improve efficiency, and strengthen the credibility of the relationship for both audiences.",
				],
			},
			{
				id: "legal",
				question: "Should I seek legal advice when doing collaborations?",
				answer: [
					"It depends on the collaboration type. If the arrangement involves more complexity, shared assets, or intellectual property, legal advice is usually sensible.",
					"For example, a co-branded product is the kind of activity where formal advice can help protect your IP and clarify responsibilities early.",
					"<em>PartnerUp has teamed up with Farringford Legal, specialist SME legal advisors to offer advice before you jump into your new business venture or collaboration.</em>",
					"<em>For a free 30 minute consultation with the Farringford Legal team to establish your immediate needs, contact <a href='mailto:info@farringfordlegal.co.uk'>info@farringfordlegal.co.uk</a> mentioning 'PartnerUp' in your message.</em>",
				],
			},
			{
				id: "after",
				question: "What should I do once the collaboration has finished?",
				answer: [
					"Following up with the brands involved is one of the most valuable parts of the process. Share any results you have, ask for their insights in return, and use the data to improve what you do next.",
					"When you review the collaboration, look back at the objectives you set and identify both the strongest outcomes and what you would change next time.",
				],
				list: [
					"Did we achieve the objectives we set out to accomplish?",
					"What were the strongest outcomes of our collaboration?",
					"What could have been done better or differently?",
				],
			},
		];

		const handleClose = () => {
			store.commit("general/closeCollabHelpNeverDonePopup");
		};

		const handleGoTo = (url) => {
			router.push(url);
			handleClose();
		};

		const toggleFaq = (id) => {
			if (openFaqIds.value.includes(id)) {
				openFaqIds.value = [];
				return;
			}

			openFaqIds.value = [id];
		};

		const isFaqOpen = (id) => openFaqIds.value.includes(id);

		const toggleGuideSection = (id) => {
			if (openGuideSectionIds.value.includes(id)) {
				openGuideSectionIds.value = openGuideSectionIds.value.filter((openId) => openId !== id);
				return;
			}

			openGuideSectionIds.value = [...openGuideSectionIds.value, id];
		};

		const isGuideSectionOpen = (id) => openGuideSectionIds.value.includes(id);

		const getGuideSectionToggleLabel = (section) => {
			if (section.example?.length > 1) {
				return section.tip ? "View examples and top tip" : "View examples";
			}

			if (section.example?.length === 1 || section.placeholder) {
				return section.tip ? "View example and top tip" : "View example";
			}

			if (section.tip) {
				return "View top tip";
			}

			return "View more";
		};

		return {
			popup,
			handleClose,
			handleGoTo,
			guideSections,
			starterCollabs,
			faqItems,
			toggleFaq,
			isFaqOpen,
			toggleGuideSection,
			isGuideSectionOpen,
			getGuideSectionToggleLabel,
		};
	},
};
</script>

<style lang="less" scoped>
@import "./../../assets/css/less/_settings.less";
@import "./../../assets/css/less/_helper.less";

.collab-guide {
	padding: 0;
	.main-title {
		margin: 0 40px 10px 0;
	}
}

.guide-hero {
	margin: 14px 0 22px;
	padding: 6px 6px 18px;
	border-bottom: 1px solid @guidedRoseBorder;
}

.guide-hero-copy {
	display: flex;
	align-items: center;
}

.guide-intro {
	margin: 0;
	font-size: 1.02rem;
	line-height: 1.6;
	color: @darkContrast;
	max-width: 780px;
}

.side-panel,
.guide-section {
	border: 1px solid @guidedRoseBorder;
	border-radius: @radiusXl;
	background: #ffffff;
	.elevation.card();
}

.tip {
	position: relative;
}

.tip-label {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	position: absolute;
	top: 0;
	right: 0;
	background: @guidedRose;
	color: #fff;
	font-size: 0.6rem;
	font-weight: 800;
	letter-spacing: 0.02em;
	padding: 4px 10px;
	border-radius: 0 @radiusLg 0 @radiusLg;
	.elevation.editorialBadge();
	white-space: nowrap;
	margin: 0;
}

.hero-tip,
.side-tip,
.callout-box--tip {
	align-self: start;
	padding: 0;
	border: 0;
	background: transparent;
	box-shadow: none;
}

.tip-panel {
	width: 100%;
	padding: 28px 14px 12px 14px;
	border-radius: @radiusLg;
	border: 1px solid @guidedRoseBorder;
	background: #fff;

	p {
		margin: 0;
		color: @guidedRoseText;
		line-height: 1.35;
		font-weight: @bold;

		.link {
			color: inherit;
			text-decoration: underline;
			cursor: pointer;
		}
	}
}

.guide-layout {
	display: grid;
	grid-template-columns: minmax(0, 1.15fr) minmax(340px, 0.85fr);
	gap: 22px;
	align-items: start;
}

.guide-column {
	display: flex;
	flex-direction: column;
	gap: 18px;
	min-width: 0;
}

.guide-section {
	padding: 18px;
}

.guide-section--reverse {
	.section-heading {
		grid-template-columns: minmax(0, 1fr) 118px;
	}

	.section-visual {
		order: 2;
	}

	.section-copy {
		order: 1;
	}
}

.section-heading {
	display: grid;
	grid-template-columns: 118px minmax(0, 1fr);
	gap: 18px;
	align-items: start;
}

.section-visual {
	display: flex;
	align-items: flex-start;
	justify-content: center;
}

.visual-card {
	width: 118px;
	height: 118px;
	border-radius: @radiusLg;
	padding: 0;
	background: #ffffff;
	border: 1px solid @guidedRoseBorderStrong;
	.elevation.card();
	overflow: hidden;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
		object-position: -7px center;
	}
}

.section-copy {
	h3 {
		margin: 0 0 10px;
		font-size: 1.35rem;
		line-height: 1.2;
		color: @darkContrast;
	}

	p {
		margin: 0 0 10px;
		color: fade(@darkContrast, 78%);
		line-height: 1.58;
		font-size: 0.93rem;
	}

	p:last-child {
		margin-bottom: 0;
	}
}

.section-copy-extra {
	margin-top: 10px;
}

.guide-toggle {
	margin-top: 0;
	padding: 0;
	border: 0;
	background: transparent;
	color: @guidedRoseText;
	font-size: 0.9rem;
	font-weight: 800;
	line-height: 1.4;
	text-decoration: underline;
	text-underline-offset: 0.14em;
	cursor: pointer;
}

.callout-box {
	margin-top: 16px;
	padding: 14px 16px;
	border: 1px solid @guidedRoseBorder;
	border-radius: @radiusLg;
	background: linear-gradient(180deg, @guidedRoseBgStrong, @guidedRoseBgSubtle);
	.elevation.card();

	p {
		&:last-child {
			margin-bottom: 0;
		}
		&:first-child,
		&.example-text {
			margin-top: 0;
		}
		&.example-text {
			font-style: italic;
			font-size: 0.93rem;
		}
	}
}

.callout-box--tip {
	padding: 0;
	border: 0;
	background: transparent;
	box-shadow: none;
}

.callout-title {
	margin: 0 0 4px;
	font-size: 0.82rem;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	font-weight: 800;
	color: @guidedRoseText;
}

.callout-list,
.faq-bullets {
	list-style: none;
	margin: 8px 0 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 8px;

	li {
		position: relative;
		padding-left: 14px;
		line-height: 1.55;
		color: fade(@darkContrast, 82%);
		font-size: 0.9rem;

		strong {
			font-weight: @bold;
		}

		a {
			color: inherit;
			text-decoration: underline;
			cursor: pointer;
		}

		em {
			font-style: italic;
			color: inherit;
		}

		&:before {
			content: "";
			position: absolute;
			left: 0;
			top: 0.72em;
			width: 6px;
			height: 6px;
			border-radius: @radiusCircle;
			background: @guidedRoseBorderStrong;
			transform: translateY(-50%);
		}
	}
}

.side-panel {
	padding: 18px;
}

.panel-header {
	margin-bottom: 14px;

	h3 {
		margin: 0;
		font-size: 1.4rem;
		line-height: 1.2;
		color: @darkContrast;
	}
}

.starter-typess {
	:deep(.types) {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 10px;
		align-items: stretch;

		.type {
			width: auto;
			height: 100%;
			margin: 0;
			min-height: 0;
			padding: 16px 12px;
			border-width: 2px;
			aspect-ratio: 1 / 0.35;

			span {
				font-size: 1rem;
				padding: 12px;
			}
		}
	}

	:deep(.type:nth-child(3n - 1)),
	:deep(.type:nth-child(-n + 3)),
	:deep(.type:nth-child(even)),
	:deep(.type:first-child) {
		margin: 0;
	}
}

.side-tip {
	margin-top: 16px;
}

.faq-list {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.faq-item {
	border: 1px solid @guidedRoseBorder;
	border-radius: @radiusLg;
	background: #ffffff;
	overflow: hidden;
}

.faq-trigger {
	width: 100%;
	border: 0;
	background: transparent;
	padding: 13px 15px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	text-align: left;
	font-size: 0.95rem;
	font-weight: @bold;
	color: @darkContrast;
	cursor: pointer;
}

.faq-icon {
	flex: 0 0 auto;
	font-size: 1.25rem;
	line-height: 1;
	color: @guidedRoseText;
}

.faq-body {
	padding: 0 16px 16px;

	p {
		margin: 0 0 10px;
		line-height: 1.52;
		color: fade(@darkContrast, 82%);
		font-size: 0.9rem;
		:deep(a) {
			color: inherit;
			text-decoration: underline;
		}
	}

	p:last-child {
		margin-bottom: 0;
	}
}

@media screen and (max-width: 1180px) {
	.guide-layout {
		grid-template-columns: minmax(0, 1fr);
	}
}

@media screen and (max-width: 900px) {
	.guide-hero {
		grid-template-columns: minmax(0, 1fr);
	}
}

@media screen and (max-width: 640px) {
	.collab-guide {
		padding: 0 0 18px;
	}

	.guide-section,
	.side-panel {
		padding: 16px;
		border-radius: @radiusLg;
	}

	.section-heading {
		grid-template-columns: minmax(0, 1fr);
	}

	.guide-section--reverse {
		.section-heading {
			grid-template-columns: minmax(0, 1fr);
		}

		.section-visual,
		.section-copy {
			order: initial;
		}
	}

	.section-visual {
		justify-content: flex-start;
	}

	.panel-header h3,
	.section-copy h3 {
		font-size: 1.2rem;
	}

	.starter-types2 {
		:deep(.types) {
			grid-template-columns: minmax(0, 1fr);
		}

		:deep(.type) {
			min-height: 92px;
			margin: 0 !important;
		}
	}
}

@media screen and (min-width: 1440px) {
	.tip-panel {
		padding: 12px 80px 12px 14px;
	}
}
</style>